import { getAdminQuestionnaires } from '@/_api/admin-questionnaires';
import type { QuestionnaireIndexSerializer } from '@/_types/serializers/v1/questionnaires';

// NOTE from Jordan: using this approach because when there are more than 100 questionnaires,
// we often can't load them all within 30 seconds (the Heroku default timeout). So we're instead
// asking for 50 at a time and then combining them together after.
export const getAllQuestionnaires = async (
  filter: any,
  totalCount: number,
  token: string,
) => {
  const rowsPerPage = 50;
  const params = { ...filter };

  async function getQuestionnaires(page: number) {
    params.page = page - 1;
    params.rows_per_page = rowsPerPage;
    return await getAdminQuestionnaires({
      ...params,
      token,
    });
  }

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const promises: Array<() => Promise<any>> = [];

  for (let i = 1; i <= totalPages; i++) {
    promises.push(async () => await getQuestionnaires(i));
  }

  // We'd only want to, max, ask for 200 (50 * 4) at a time
  const batchSize = 4;

  async function runBatch(startOfBatch: number): Promise<any> {
    const batchPromises = promises.slice(
      startOfBatch,
      startOfBatch + batchSize,
    );
    const batchOfQuestionnaires = await Promise.all(
      batchPromises.map((p) => p()),
    );

    return batchOfQuestionnaires.flat(Infinity);
  }

  const batchPromises: Array<() => Promise<QuestionnaireIndexSerializer>> = [];

  for (let i = 0; i < promises.length; i += batchSize) {
    batchPromises.push(async () => await runBatch(i));
  }

  const questionnaires: Array<QuestionnaireIndexSerializer> = [];

  await (async function loop() {
    for (let i = 0; i < batchPromises.length; i++) {
      const result = await batchPromises[i]();
      questionnaires.push(result as QuestionnaireIndexSerializer);
    }
  })();

  return questionnaires.flat(Infinity);
};
